import React, { useCallback, useContext, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import {
  Tooltip as BaseTooltip,
  Divider as BaseDivider,
  FlexColumn,
  FlexSpace,
  Flex,
  AttentionBox as AttentionBoxBase
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { ReactComponent as BaseNoShowIcon } from '@ubeya/shared-web/assets/no-show-red.svg';
import { ReactComponent as BaseStarIcon } from '@ubeya/shared-web/assets/star-filled-primary.svg';
import { ago } from '@ubeya/shared/utils/time';
import MatchPill from '../MatchPill';

const TooltipCard = styled.div`
  width: 402px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: ${({ theme }) => `0 2px 10px ${theme.colors.grey2}`};
  padding: 16px 4px 16px 16px;
  position: relative;
  pointer-events: auto;
`;

const CardHeader = styled(Flex)`
  margin-bottom: 8px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  width: 370px;
`;

const EmployeeName = styled.h3`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  padding: 0;
`;

const CardDivider = styled(BaseDivider)`
  margin: 8px 0 8px 0;
  width: calc(100% - 16px);
  background-color: ${({ theme }) => theme.colors.grey2};
`;

const AttentionBox = styled(AttentionBoxBase)`
  padding: 8px;
`;

const StatsRows = styled(FlexColumn)`
  gap: 12px;
  overflow-y: auto;
  max-height: 215px;
  padding-right: 9px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.white};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grey2};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.grey1};
  }
`;

const StatRow = styled(FlexColumn)`
  gap: 4px;
`;

const StatRowHeader = styled(FlexSpace)`
  width: 100%;
`;

const NoShowWrapper = styled(Flex)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.black};
  flex: 0 1 auto;
  margin-right: 8px;
`;

const StatValue = styled.div`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.black};
  text-align: right;
  white-space: nowrap;
  flex: 0 0 auto;
`;

const StatBarTrack = styled.div`
  height: 8px;
  background: ${({ theme }) => theme.colors.grey2};
  border-radius: 50px;
  overflow: hidden;
`;

const StatBarFill = styled.div`
  height: 100%;
  width: ${({ $percent }) => `${$percent}%`};
  border-radius: inherit;
  background-color: ${({ $percent, theme, $isPositiveFactor }) => {
    if (!$percent) return theme.colors.grey2;
    if ($isPositiveFactor) return theme.colors.greenPositive;
    if ($percent < 25) return theme.colors.redNegative;
    if ($percent < 50) return theme.colors.yellowPartially;
    return theme.colors.greenPositive;
  }};
`;

const LeftSideHeader = styled(Flex)`
  flex-direction: row;
  gap: 8px;
`;

const RightSideHeader = styled(Flex)`
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

const statsIcon = css`
  width: 16px;
  height: 16px;
`;

const NoShowIcon = styled(BaseNoShowIcon)`
  ${statsIcon};
`;

const StarIcon = styled(BaseStarIcon)`
  ${statsIcon};
  margin-bottom: 2px;
`;

const Separator = styled.div`
  background-color: ${({ theme }) => theme.colors.grey2};
  width: 1px;
  height: 16px;
  flex-shrink: 0;
`;

const Tooltip = styled(BaseTooltip)`
  opacity: 1;
  background: transparent;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15));

  .rc-tooltip-inner {
    padding: 0;
    background: transparent;
    box-shadow: none;
    border: none;
    opacity: 1;
    margin: ${({ $withMargin }) => ($withMargin ? '20px 0 20px 44px' : '5px 0 5px 44px')};
  }

  .rc-tooltip-content {
    padding: 0;
    opacity: 1;
  }

  .rc-tooltip-arrow {
    z-index: 1;
    border-color: white;
    opacity: 1;
    margin: ${({ $withMargin }) => ($withMargin ? '20px 0px' : '5px 0px')};
    filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.25));
    border-width: 8px !important;
  }

  /* Specific arrow positioning styles */
  &.rc-tooltip-placement-top .rc-tooltip-arrow,
  &.rc-tooltip-placement-topLeft .rc-tooltip-arrow,
  &.rc-tooltip-placement-topRight .rc-tooltip-arrow {
    border-top-color: white;
    border-left-color: transparent;
    border-right-color: transparent;
    border-bottom: none;
  }

  &.rc-tooltip-placement-bottom .rc-tooltip-arrow,
  &.rc-tooltip-placement-bottomLeft .rc-tooltip-arrow,
  &.rc-tooltip-placement-bottomRight .rc-tooltip-arrow {
    border-bottom-color: white;
    border-left-color: transparent;
    border-right-color: transparent;
    border-top: none;
  }
`;

//disable the event to be triggered when clicking inside the tooltip
const handleCardClick = (e) => {
  e.stopPropagation();
  e.preventDefault();
};

const CandidateStats = ({ name, isCrown, match, stats = {}, isRookie }) => {
  const { t } = useContext(ConfigContext);
  const clientStats = stats?.client || {};
  const rankingStats = stats?.ranking || {};

  const hasTrackedScroll = useRef(false);

  const handleScroll = useCallback(() => {
    if (hasTrackedScroll.current) return;
    hasTrackedScroll.current = true;
    trackEvent('employee new jobs tooltip - User scrolled', {
      module: 'Scheduling',
      context: 'Manage Booking',
      employeeId: stats?.id
    });
  }, [stats?.id]);

  // Helper function to check if value is a date in DD/MM/YYYY format
  const isDateValue = (value) => {
    if (!value || typeof value !== 'string') return false;
    // Check for YYYY-MM-DD format (ISO date-only)
    return moment(value, 'YYYY-MM-DD', true).isValid();
  };

  // Convert stats object to array for rendering if it's an object
  const rankingStatsArray = Object.entries(rankingStats).map(([label, stat]) => ({
    label,
    ...stat
  }));

  return (
    <TooltipCard onClick={handleCardClick}>
      <CardHeader>
        <LeftSideHeader>
          <EmployeeName>{name}</EmployeeName>
        </LeftSideHeader>
        <RightSideHeader>
          {stats?.totalNoShows && (
            <BaseTooltip
              body={t('employeeNoShowTooltip', {
                firstName: name,
                noShowsCount: stats.totalNoShows,
                shiftCount: (stats.totalBooked || 0) + stats.totalNoShows
              })}>
              <NoShowWrapper>
                <NoShowIcon />
                <StatValue>{`${stats.totalNoShows}/${(stats.totalBooked || 0) + stats.totalNoShows}`}</StatValue>
                <Separator />
              </NoShowWrapper>
            </BaseTooltip>
          )}
          {stats?.avgRating && (
            <>
              <StarIcon />
              <StatValue>{`${stats.avgRating}`}</StatValue>
              <Separator />
            </>
          )}
          <MatchPill
            match={match}
            text={isRookie ? t('earlyScore') : ''}
            isCrown={isCrown}
            isRookie={isRookie}
            isClientPreferred={clientStats?.isPreferred}
            withTooltip
          />
        </RightSideHeader>
      </CardHeader>
      <CardDivider />
      <StatsRows onScroll={handleScroll}>
        {isRookie && <AttentionBox>{t('earlyScoreTooltip')}</AttentionBox>}
        {rankingStatsArray.map((stat) => {
          const isDate = isDateValue(stat.value);
          const valueSlug = isDate ? ago(stat.value, t) : stat.valueSlug;
          return (
            <StatRow key={stat.label}>
              <StatRowHeader>
                <StatLabel>{t(stat.label)}</StatLabel>
                <StatValue>{t(valueSlug, { value: stat.value, score: stat.score, total: stat?.total })}</StatValue>
              </StatRowHeader>
              <StatBarTrack aria-hidden="true">
                <StatBarFill $percent={stat.score} $isPositiveFactor={stat.isPositiveFactor} />
              </StatBarTrack>
            </StatRow>
          );
        })}
      </StatsRows>
    </TooltipCard>
  );
};

const LeafStats = ({ name }) => {
  const { t } = useContext(ConfigContext);

  return (
    <TooltipCard onClick={handleCardClick}>
      <CardHeader>
        <EmployeeName>{name}</EmployeeName>
        <MatchPill isLeaf match={0} text={t('new')} withTooltip />
      </CardHeader>

      <CardDivider />

      <StatsRows>
        <StatLabel>{t('newWorkerNoDataYet')}</StatLabel>
      </StatsRows>
    </TooltipCard>
  );
};

const ExternalStats = ({ name, stats }) => {
  const { t } = useContext(ConfigContext);

  return (
    <TooltipCard onClick={handleCardClick}>
      <CardHeader>
        <EmployeeName>{name}</EmployeeName>
        <MatchPill text={t('external')} isExternal isClientPreferred={stats?.client?.isPreferred} />
      </CardHeader>

      <CardDivider />

      <StatsRows>
        <StatLabel>{t('externalEmployeeNoData')}</StatLabel>
      </StatsRows>
    </TooltipCard>
  );
};

const TooltipContent = ({ stats, isLeaf, isRookie, isCrown, name, match, isExternal }) => {
  useEffect(() => {
    if (isExternal) {
      trackEvent('Manage booking - external employee tooltip opened', {
        module: 'Scheduling',
        context: 'Manage Booking',
        name,
        stats: stats || {}
      });
      return;
    }
    if (!stats || Object.keys(stats).length === 0) return;
    trackEvent('Manage booking - employee new jobs tooltip opened', {
      module: 'Scheduling',
      context: 'Manage Booking',
      employeeId: stats?.id,
      totalBooked: stats?.totalBooked,
      chosenMatch: stats?.chosenMatch,
      clientPreferredStatus: stats?.client?.isPreferred,
      ranking: stats?.ranking || {}
    });
  }, [stats, isExternal, name]);
  if (isLeaf) return <LeafStats name={name} />;
  if (isExternal) return <ExternalStats name={name} stats={stats} />;
  return <CandidateStats name={name} isCrown={isCrown} match={match} stats={stats} isRookie={isRookie} />;
};

const EmployeeStatsPopover = ({
  trigger,
  name,
  stats,
  isLeaf = false,
  isRookie = false,
  isCrown = false,
  isExternal,
  withMargin = false,
  isDisabled = false
}) => {
  const match = stats?.chosenMatch || 0;

  return (
    <Tooltip
      $withMargin={withMargin}
      disable={isDisabled || match === -1}
      body={
        <TooltipContent
          stats={stats}
          isLeaf={isLeaf}
          isRookie={isRookie}
          isCrown={isCrown}
          name={name}
          match={match}
          isExternal={isExternal}
        />
      }>
      {trigger}
    </Tooltip>
  );
};

export default EmployeeStatsPopover;
